import type { PricingMapType } from "../types";

/**
 * Result of extracting full pricing data
 */
export interface ExtractedPricingData {
  price: number;
  pricedTreatmentId: number;
  mode: "CASH" | "INSURED";
  paymentType: "SELFPAY" | "INSURANCE";
}

/**
 * Extracts the price from the pricing map based on the selected HRT.
 * Uses the provided modeOverride if given, otherwise falls back to
 * the pricing mode from the priceObject itself, then to "CASH".
 *
 * @param pricingMap - The pricing map containing prices for different HRTs
 * @param hrtId - The HRT ID to look up pricing for
 * @param modeOverride - Optional pricing mode to use instead of pricingMap mode
 * @returns The price for the given HRT, or 0 if not found
 */
export function getPriceFromPricingMap(
  pricingMap: PricingMapType | undefined,
  hrtId: number | string | undefined,
  modeOverride?: "CASH" | "INSURED",
): number {
  const priceObject = pricingMap?.[hrtId ?? ""];
  const pricingMode = modeOverride ?? priceObject?.mode ?? "CASH";
  const price = priceObject?.priceData[pricingMode]?.price ?? 0;
  return price;
}

/**
 * Extracts full pricing data including pricedTreatmentId.
 * Consolidates the repeated pattern of extracting price, pricedTreatmentId, and mode.
 */
export function extractPricingData(
  pricingMap: PricingMapType | undefined,
  hrtId: number | string | undefined,
  contextPricingMode?: string,
): ExtractedPricingData {
  const priceObject = pricingMap?.[hrtId ?? ""];
  const mode = (contextPricingMode ?? priceObject?.mode ?? "CASH") as
    | "CASH"
    | "INSURED";
  const priceData = priceObject?.priceData?.[mode];

  return {
    price: priceData?.price ?? 0,
    pricedTreatmentId: priceData?.pricedTreatmentId ?? 0,
    mode,
    paymentType: mode === "INSURED" ? "INSURANCE" : "SELFPAY",
  };
}
